// src/components/artists/ArtistSearch.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  CheckCircleIcon, 
  UserIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { searchArtists, SpotifyArtist, linkArtistToUser } from '../../services/spotifyArtistService';
import { isAuthenticated, refreshAccessToken } from '../../services/authService';
import LoadingState from '../common/LoadingState';

interface ArtistSearchProps {
  onArtistSelected?: (artist: SpotifyArtist) => void;
  showLinkButton?: boolean;
  redirectAfterLink?: string;
}

const ArtistSearch: React.FC<ArtistSearchProps> = ({ 
  onArtistSelected,
  showLinkButton = true,
  redirectAfterLink
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SpotifyArtist[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<SpotifyArtist | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<boolean>(false);
  const [isLinking, setIsLinking] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Effekt zum Löschen der Erfolgsmeldung nach 3 Sekunden
  useEffect(() => {
    if (linkSuccess) {
      const timer = setTimeout(() => {
        setLinkSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [linkSuccess]);

  // Effekt zum erneuten Versuch der Auth, wenn ein Auth-Fehler auftritt
  useEffect(() => {
    if (error && error.includes('authentication') && retryCount < 2) {
      const retryAuth = async () => {
        try {
          // Versuche das Token zu aktualisieren
          const success = await refreshAccessToken();
          if (success) {
            console.log("Token aktualisiert, versuche erneut zu suchen...");
            setError(null);
            setRetryCount(prev => prev + 1);
            
            // Wenn eine Suche vorhanden war, diese wiederholen
            if (query) {
              handleSearch();
            }
          }
        } catch (err) {
          console.error("Fehler beim erneuten Verbindungsaufbau:", err);
        }
      };
      
      // Kurz warten und dann Authentifizierung erneut versuchen
      const timer = setTimeout(() => {
        retryAuth();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [error, retryCount, query]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    try {
      setIsSearching(true);
      setError(null);
      
      const results = await searchArtists(query);
      setSearchResults(results);
      
      if (results.length === 0) {
        setError('No artists found. Please try a different search term.');
      }
    } catch (err) {
      console.error('Error during artist search:', err);
      setError('An error occurred during search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectArtist = (artist: SpotifyArtist) => {
    setSelectedArtist(artist);
    
    if (onArtistSelected) {
      onArtistSelected(artist);
    }
  };

  const handleLinkArtist = async (artist: SpotifyArtist) => {
    console.log("handleLinkArtist called, artist:", artist);
    if (!artist) return;
  
    if (!isAuthenticated()) {
      sessionStorage.setItem('selectedArtist', JSON.stringify(artist));
      navigate('/login', { state: { returnPath: window.location.pathname } });
      return;
    }
  
    try {
      setIsLinking(true);
      setError(null);
  
      const genre = artist.genres && artist.genres.length > 0 
        ? artist.genres[0] 
        : 'Unknown';
  
      const success = await linkArtistToUser(artist.id, artist.name, genre);
  
      if (success) {
        setSelectedArtist(artist); // Jetzt hier sicher setzen
        setLinkSuccess(true);
  
        if (redirectAfterLink) {
          setTimeout(() => {
            navigate(redirectAfterLink);
          }, 1500);
        }
      } else {
        setError('Could not link the artist. Please try again.');
      }
    } catch (err) {
      console.error('Error linking artist:', err);
      setError('An error occurred while linking the artist. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };
  

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Artist Search</h2>
      
      {/* Suchbox */}
      <div className="mb-6">
        <div className="flex">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter artist name..."
            className="block flex-grow px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !query.trim()}
            className="bg-green-600 text-white px-4 py-2 rounded-r-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
        </div>
        {error && (
          <div className="mt-2 flex items-start text-sm text-red-600">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
      </div>
      
      {/* Ladeanimation während der Suche */}
      {isSearching && (
        <div className="my-4">
          <LoadingState message="Searching..." size="small" />
        </div>
      )}
      
      {/* Ausgewählter Künstler */}
      {selectedArtist && (
        <div className="mb-6 p-4 border border-green-200 bg-green-50 rounded-lg">
          <h3 className="text-md font-semibold text-gray-700 mb-2">Selected Artist</h3>
          <div className="flex items-center">
            {selectedArtist.images && selectedArtist.images[0] ? (
              <img 
                src={selectedArtist.images[0].url} 
                alt={selectedArtist.name} 
                className="h-16 w-16 rounded-full object-cover mr-4"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                <UserIcon className="h-8 w-8 text-gray-400" />
              </div>
            )}
            <div>
              <h4 className="font-medium text-gray-900">{selectedArtist.name}</h4>
              <p className="text-sm text-gray-500">
                {selectedArtist.followers.total.toLocaleString()} Followers
                {selectedArtist.genres && selectedArtist.genres.length > 0 && (
                  <span> • {selectedArtist.genres.slice(0, 2).join(', ')}</span>
                )}
              </p>
            </div>
          </div>
          
          
        </div>
      )}
      
      {/* Suchergebnisse */}
      
{searchResults.length > 0 && (
  <div>
    <h3 className="text-md font-semibold text-gray-700 mb-2">Search Results</h3>
    <div className="divide-y divide-gray-200">
      {searchResults.map(artist => (
        <div 
          key={artist.id}
          className={`py-3 flex items-center ${
            selectedArtist?.id === artist.id ? 'bg-green-50' : ''
          }`}
        >
          {/* Artist-Bild und Informationen */}
          <div
            className="flex-grow flex items-center cursor-pointer"
            onClick={() => handleSelectArtist(artist)}
          >
            {artist.images && artist.images[0] ? (
              <img 
                src={artist.images[0].url} 
                alt={artist.name} 
                className="h-12 w-12 rounded-full object-cover mr-4"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                <UserIcon className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div>
              <h4 className="font-medium text-gray-900">{artist.name}</h4>
              <p className="text-sm text-gray-500">
                {artist.followers.total.toLocaleString()} Followers
                {artist.genres && artist.genres.length > 0 && (
                  <span> • {artist.genres.slice(0, 2).join(', ')}</span>
                )}
              </p>
            </div>
          </div>

          {/* Link-Button für jeden Künstler */}
          <button
            onClick={() => handleLinkArtist(artist)}
            className="ml-2 px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
          >
            Link
          </button>

          {/* Haken-Icon bei Auswahl */}
          {selectedArtist?.id === artist.id && (
            <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
          )}
        </div>
      ))}
    </div>
  </div>
)}

    </div>
  );
};

export default ArtistSearch;